import { auth } from "@/firebase";
import Vue from "vue";
import VueRouter from "vue-router";
import VueGoodTablePlugin from "vue-good-table";
import "vue-good-table/dist/vue-good-table.css";
import "@/../public/css/components/vue-good-table.css";
import localforage from "localforage";
import store from "@/store/index.js";
import Swal from "sweetalert2";
import CacheAdministrativoService from "@/services/CacheAdministrativoService.js";
import { MODO_MUESTRA as MODO_MUESTRA_INFORME_PESV } from "@/services/informePesv/modoMuestra";
import { esEmbebido } from "@/utils/embebido";
import { obtenerSalidaDelConductor } from "@/services/traslados";
import { esAuditorSofu } from "@/utils/auditorSofu";
Vue.use(VueRouter);
Vue.use(VueGoodTablePlugin);

// CONTENEDOR DINAMICO
const DynamicWrapper = {
  functional: true,
  render(h, { props, data }) {
    const component = props.component;
    return h(component, data);
  },
};

// Dígitos del NIT, para comparar sin importar si quedó guardado como número o como texto
const digitosNit = (empresa) => String((empresa && empresa.nit && empresa.nit.numero) || "").replace(/\D/g, "");

/**
 * Qué se abre en la hoja de vida de un conductor (el id llega en la URL). Con el privilegio de editar
 * conductores se edita; con el de ver, se consulta (`meta.consulta`, con el motivo):
 * - La propia: para editar.
 * - La de un conductor del espacio seleccionado (motivo «privilegio» si solo puede ver).
 * - Con el panel administrativo, las de los otros espacios del mismo NIT, como en la Plantilla de conductores
 *   (motivo «panel» si solo puede ver).
 * - Desde el espacio del que el conductor salió por traslado: solo consulta (motivo «traslado»), con los
 *   documentos que se cargaron en ese espacio. El Registro de traslados la abre así con `?modo=consulta`.
 * - Cualquier otra: no se abre.
 * @return {Promise<Function>} El componente de la ruta.
 */
async function vistaHojaDeVidaConductor(to, usuario, idEmpresaSeleccionada) {
  const hojaDeVida = () => import("../views/SeguridadVial/Conductores/Conductor.vue");
  const sinPrivilegios = () => import("@/components/Alertas/SinPrivilegios.vue");
  const fueraDelEspacio = () => import("@/components/Alertas/ConductorFueraDelEspacio.vue");
  const consultar = (motivo, salida = null) => {
    to.meta.consulta = { motivo, salida };
    return hojaDeVida;
  };
  const [obtenerVinculacion, obtenerConductor] = await Promise.all([
    store.state.obtenerVinculacion(idEmpresaSeleccionada, usuario.id),
    store.state.obtenerConductor(to.params.id),
  ]);
  if (!obtenerConductor) return fueraDelEspacio;
  const privilegios = obtenerVinculacion && obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios
    ? obtenerVinculacion.privilegios
    : {};
  const conductores = (privilegios.seguridadVial && privilegios.seguridadVial.conductores) || {};
  const puedeVer = !!(conductores.ver || conductores.editar);
  const esConductorConRodamiento = obtenerConductor.conRodamiento === "Si" && obtenerConductor.idUsuario === usuario.id;
  // Un documento en 0 no identifica a nadie: hay perfiles y conductores que todavía no tienen el número
  const numeroDocumento = Number(usuario.documento && usuario.documento.numero) || 0;
  const esConductorSinRodamiento = obtenerConductor.conRodamiento === "No" && numeroDocumento > 0 &&
    !!obtenerConductor.sinRodamiento && !!obtenerConductor.sinRodamiento.documento &&
    Number(obtenerConductor.sinRodamiento.documento.numero) === numeroDocumento;
  if (esConductorConRodamiento || esConductorSinRodamiento) return hojaDeVida;
  if (obtenerConductor.idEmpresa === idEmpresaSeleccionada) {
    if (conductores.editar) return hojaDeVida;
    return puedeVer ? consultar("privilegio") : sinPrivilegios;
  }
  const pideConsulta = to.query.modo === "consulta";
  const buscarSalida = () => obtenerSalidaDelConductor(idEmpresaSeleccionada, obtenerConductor.id);
  // Desde el Registro de traslados, la de quien salió se consulta con los documentos de este espacio
  const salidaPedida = pideConsulta ? await buscarSalida() : null;
  if (salidaPedida) return puedeVer ? consultar("traslado", salidaPedida) : sinPrivilegios;
  if (puedeVer && privilegios.panelAdministrativo && privilegios.panelAdministrativo.ver) {
    const [empresaSeleccionada, empresaConductor] = await Promise.all([
      store.state.obtenerEmpresa(idEmpresaSeleccionada),
      store.state.obtenerEmpresa(obtenerConductor.idEmpresa),
    ]);
    if (digitosNit(empresaSeleccionada) && digitosNit(empresaSeleccionada) === digitosNit(empresaConductor)) {
      return conductores.editar && !pideConsulta ? hojaDeVida : consultar("panel");
    }
  }
  const salida = pideConsulta ? null : await buscarSalida();
  if (salida) return puedeVer ? consultar("traslado", salida) : sinPrivilegios;
  return fueraDelEspacio;
}

const routes = [
  /* LOADING TO - SERVICE WORKER - PUENTE PARA REDIRECCIONAR AL INICIO */
  {
    path: "/",
    redirect: "/entrada",
  },
  // Entrada - Pantalla de Entrada
  {
    path: "/entrada",
    name: "Entrada",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      to.meta.component = () => import("../views/Entrada.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  // Inicio - Inicio Principal de la App
  {
    path: "/inicio",
    name: "Inicio",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      // const obtenerUsuario = auth.currentUser ? await store.state.obtenerUsuario(auth.currentUser.uid) : null;
      // if (obtenerUsuario && obtenerUsuario.preferencias.idEmpresaSeleccionada) {
      //   const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
      //   if (obtenerVinculacion.estado === "Vinculado" && (obtenerVinculacion.privilegios.panelAdministrativo && obtenerVinculacion.privilegios.panelAdministrativo.ver)) {
      //     to.meta.component = () => import("../views/Administracion/Inicio.vue");
      //   } else {
      //     const obtenerConductorConRodamiento = await store.state.obtenerConductorIdUsuarioIdEmpresa(obtenerUsuario.id, obtenerUsuario.preferencias.idEmpresaSeleccionada);
      //     const obtenerConductorSinRodamiento = !obtenerConductorConRodamiento ? await store.state.obtenerConductorSinRodamientoNumeroIdentificacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.documento.numero) : null;
      //     if (obtenerVinculacion.estado === "Vinculado" && (obtenerConductorConRodamiento || obtenerConductorSinRodamiento)) {
      //       to.meta.component = () => import("../views/SeguridadVial/Inicio.vue");
      //     } else {
      //       to.meta.component = () => import("../views/Inicio.vue");
      //     }
      //   }
      // } else {
      //   to.meta.component = () => import("../views/Inicio.vue");
      // }
      to.meta.component = () => import("../views/Inicio.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  // ADMINISTRACIÓN - INICIO
  {
    path: "/administracion/inicio",
    name: "AdministracionInicio",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.panelAdministrativo && obtenerVinculacion.privilegios.panelAdministrativo.ver) {
            to.meta.component = () => import("../views/Administracion/Inicio.vue");
          } else {
            to.meta.component = () => import("../components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // ADMINISTRACÍÓN - FLOTA EMPRESARIAL
  {
    path: "/administracion/flotaempresarial",
    name: "AdministracionFlotaEmpresarial",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.panelAdministrativo && obtenerVinculacion.privilegios.panelAdministrativo.ver) {
            to.meta.component = () => import("../views/Administracion/FlotaEmpresarial/FlotaEmpresarial.vue");
          } else {
            to.meta.component = () => import("../components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // ADMINISTRACÍÓN - PLANTILLA CONDUCTORES
  {
    path: "/administracion/plantillaconductores",
    name: "AdministracionPlantillaConductores",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.panelAdministrativo && obtenerVinculacion.privilegios.panelAdministrativo.ver) {
            to.meta.component = () => import("../views/Administracion/PlantillaConductores/PlantillaConductores.vue");
          } else {
            to.meta.component = () => import("../components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: { 
      requiereAuth: true, 
      component: null 
    },
  },
  // MONITOREO GEOGRAFICO
  {
    path: "/monitoreogeografico",
    name: "MonitoreoGeografico",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Administracion/MonitoreoGeografico/MonitoreoGeografico.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // NOVEDADES - PARA VER LAS NUEVAS ACTUALIZACIONES DE LA APP
  {
    path: "/novedades",
    name: "Novedades",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Novedades/Novedades.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/novedades/:id",
    name: "NovedadesConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Novedades/Consultar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PRECIOS - PRECIOS DE RISKMANN
  {
    path: "/precios",
    name: "Precios",
    // component: () => import("../views/Planes.vue"),
    component: async () => {
      // Validaciones para ingresar a la vista
      // Si no hay un usuario autenticado
      /*if (!auth.currentUser) {
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      // Obtenemos la estructura del usuario
      const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      // Si el usuario no tiene estructura
      if (!usuario) {
        // Cerramos la sesion del usuario
        auth.signOut();
        // Avisamos al usuario
        store.state.alerta(
          "error",
          "Usuario dañado.",
          "Tu usuario presenta una anomalía en su estructura.",
          "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
        );
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }*/
      // Si el usuario cumple con todas las validaciones
      // Redirigimos finalmente a la vista
      return import("../views/Precios.vue");
    },
    meta: { requiereAuth: false },
  },
  {
    path: "/precios/:id",
    name: "Precio",
    // component: () => import("../views/Planes.vue"),
    component: async () => {
      // Validaciones para ingresar a la vista
      // Si no hay un usuario autenticado
      /* if (!auth.currentUser) {
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      // Obtenemos la estructura del usuario
      const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      // Si el usuario no tiene estructura
      if (!usuario) {
        // Cerramos la sesion del usuario
        auth.signOut();
        // Avisamos al usuario
        store.state.alerta(
          "error",
          "Usuario dañado.",
          "Tu usuario presenta una anomalía en su estructura.",
          "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
        );
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }*/
      // Si el usuario cumple con todas las validaciones
      // Redirigimos finalmente a la vista
      return import("../views/Precio.vue");
    },
    meta: { requiereAuth: false },
  },
  // AGENDA - PÁGINA PÚBLICA PARA AGENDAR UNA CITA CON RISKMANN
  {
    path: "/agenda-business",
    name: "PlanesPreciosAgendaBusiness",
    component: async () => {
      return import("../views/Usuario/Planes/AgendaBusiness/Agenda.vue");
    },
    meta: { requiereAuth: false },
  },
  // La dirección anterior de la agenda: la usan la landing, el CRM y los correos ya enviados. Lleva a la nueva
  // conservando la consulta (?motivo=, ?embebido=1), como hace vue-router con las redirecciones.
  {
    path: "/precios-agenda-business",
    redirect: "/agenda-business",
  },
  // ADMIN - PANEL ADMINISTRATIVO DE CITAS
  {
    path: "/admin/agenda-business",
    name: "AdminAgendaBusiness",
    component: async () => {
      // Validaciones para ingresar a la vista
      // Si no hay un usuario autenticado
      if (!auth.currentUser) {
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      // Obtenemos la estructura del usuario
      const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      // Si el usuario no tiene estructura
      if (!usuario) {
        // Cerramos la sesion del usuario
        auth.signOut();
        // Avisamos al usuario
        store.state.alerta(
          "error",
          "Usuario dañado.",
          "Tu usuario presenta una anomalía en su estructura.",
          "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
        );
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      return import("../views/Usuario/Planes/AgendaBusiness/AdminPanel.vue");
    },
    meta: { requiereAuth: true }, // Requiere autenticación, validación de admin en el componente
  },
  // THANKYOUPAGE - PAGINA DE GRACIAS Y RESUMEN DE LA ADQUISICIÓN DEL PLAN DE RISKMANN
  {
    path: "/precios/:id/thankyoupage",
    name: "PrecioThankYouPage",
    // component: () => import("../views/Planes.vue"),
    component: async () => {
      // Validaciones para ingresar a la vista
      // Si no hay un usuario autenticado
      /*if (!auth.currentUser) {
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      // Obtenemos la estructura del usuario
      const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      // Si el usuario no tiene estructura
      if (!usuario) {
        // Cerramos la sesion del usuario
        auth.signOut();
        // Avisamos al usuario
        store.state.alerta(
          "error",
          "Usuario dañado.",
          "Tu usuario presenta una anomalía en su estructura.",
          "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
        );
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }*/
      // Si el usuario cumple con todas las validaciones
      // Redirigimos finalmente a la vista
      return import("../views/ThankYouPage.vue");
    },
    meta: { requiereAuth: false },
  },
  // PLANES - PARA ADQUIRIR UN PLAN
  /*{
    path: "/planes",
    name: "Planes",
    // component: () => import("../views/Planes.vue"),
    component: async () => {
      // Validaciones para ingresar a la vista
      // Si no hay un usuario autenticado
      if (!auth.currentUser) {
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      // Obtenemos la estructura del usuario
      const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      // Si el usuario no tiene estructura
      if (!usuario) {
        // Cerramos la sesion del usuario
        auth.signOut();
        // Avisamos al usuario
        store.state.alerta(
          "error",
          "Usuario dañado.",
          "Tu usuario presenta una anomalía en su estructura.",
          "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
        );
        // Finalizamos redireccionando al login
        return import("../views/Usuario/Login.vue");
      }
      // Si el usuario cumple con todas las validaciones
      // Redirigimos finalmente a la vista
      return import("../views/Planes.vue");
    },
    meta: { requiereAuth: true },
  },*/
  /* PANEL ADMIN */
  // PANEL ADMIN - SEGURIDAD VIAL - FORMATOS
  {
    path: "/paneladministrativo/seguridadvial/formatos",
    name: "PanelAdministrativoSeguridadVialFormatos",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/SeguridadVial/Formatos/Formatos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - SEGURIDAD VIAL - EDITAR FORMATO
  {
    path: "/paneladministrativo/seguridadvial/formatos/editar/:id",
    name: "PanelAdministrativoSeguridadVialFormatosEditar",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/SeguridadVial/Formatos/Formato.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - SEGURIDAD VIAL - CONSULTAR FORMATO
  {
    path: "/paneladministrativo/seguridadvial/formatos/consultar/:id",
    name: "PanelAdministrativoSeguridadVialFormatosConsultar",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/SeguridadVial/Formatos/Formato.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - CONSOLA DE ACTIVIDAD
  {
    path: "/paneladministrativo/consolaactividad",
    name: "PanelAdministrativoConsolaActividad",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/ConsolaActividad/ConsolaActividad.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - NOVEDADES
  {
    path: "/paneladministrativo/novedades",
    name: "PanelAdministrativoNovedades",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Novedades/Novedades.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/novedades/nueva",
    name: "PanelAdministrativoNovedadesNueva",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Novedades/Novedad.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/novedades/editar/:id",
    name: "PanelAdministrativoNovedadesEditar",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Novedades/Novedad.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - EVENTOS
  {
    path: "/paneladministrativo/eventos",
    name: "PanelAdministrativoEventos",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Eventos/Eventos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/eventos/editarevento/:id",
    name: "PanelAdministrativoEventosEditar",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Eventos/Evento.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/eventos/:idEvento/chatenvivo",
    name: "PanelAdministrativoEventosChatEnVivo",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Eventos/ChatEnVivo/CreacionChatEnVivo.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - DASHBOARD ESPACIOS DE TRABAJO
  {
    path: '/paneladministrativo/dashboard',
    name: 'PanelAdministrativoDashboard',
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/DashboardEspaciosTrabajo/Dashboard.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMIN - CAPACITACIONES CERTIFICADO
  {
    path: '/paneladministrativo/certificado',
    name: 'PanelAdministrativoCertificado',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/PanelAdministrativo/Capacitaciones/Certificado/Certificado.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /* FIN - PANEL ADMIN */
  // USUARIO - EVENTOS
  {
    path: "/eventos",
    name: "Eventos",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/Eventos/Eventos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // USUARIO - EVENTO
  {
    path: "/eventos/:idEvento",
    name: "Evento",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Eventos/Evento.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // USUARIO - EVENTO - DOCUMENTOS
  {
    path: "/eventos/:idEvento/documentos",
    name: "EventoDocumentos",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/Eventos/Documentos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // USUARIO - EVENTO - AGENDAR CITA
  {
    path: "/eventos/:idEvento/agendarcita",
    name: "EventoAgendarCita",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        if (!auth.currentUser) {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        const usuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (!usuario) {
          auth.signOut();
          store.state.alerta(
            "error",
            "Usuario dañado.",
            "Tu usuario presenta una anomalía en su estructura.",
            "Tu usuario está compuesto por autentificación y estructura, hay una anomalía en la segunda. Ponte en contacto con el soporte técnico para recibir apoyo en tu caso."
          );
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
        to.meta.component = () => import("../views/Eventos/AgendarCita.vue")
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // USUARIO - EVENTOS - FORMULARIOS
  {
    path: "/eventos/:idEvento/formularios/:idFormulario",
    name: "EventosFormulario",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Eventos/Formularios/Formulario.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/eventos/:idEvento/formularios/:idFormulario/resumen/:idResumen",
    name: "EventosFormularioResumen",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Eventos/Formularios/Resumen.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMINISTRATIVO - EVENTOS - FORMULARIOS
  {
    path: "/paneladministrativo/eventos/:idEvento/formularios/:idFormulario/QR",
    name: "EventosFormularioQR",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/PanelAdministrativo/Eventos/Formularios/QR.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PANEL ADMINISTRATIVO - EVENTOS - CHAT-EN-VIVO
  {
    path: "/eventos/:idEvento/chatenvivo",
    name: "EventosChatEnVivo",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Eventos/ChatEnVivo/ChatEnVivo.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },

  // PANEL ADMINISTRATIVO - EVENTOS - FORMULARIO
  /* ESTADÍSTICAS */
  {
    path: "/eventos/:id/formularios/:idFormulario/estadisticas",
    name: "AdminEventosEstadisticas",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Estadisticas.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/eventos/:idEvento/formularios/:idFormulario/estadisticas",
    name: "AdminEventosFormularioEstadisticas",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Estadisticas.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/eventos/:id/formularios",
    name: "AdminEventosFormularios",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../components/PanelAdministrativo/Eventos/Formularios/Formularios.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/eventos/formularios/editar/:id",
    name: "AdminEventosFormulariosEditar",
    props: (route) => ({ component: route.meta.component }),
    component: DynamicWrapper,
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/PanelAdministrativo/Eventos/Formularios/Editar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/paneladministrativo/eventos/formularios/preguntas/nueva/:id",
    name: "AdminEventosFormulariosPreguntasCrear",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/PanelAdministrativo/Eventos/Formularios/Preguntas/Crear.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // FIN - FORMULARIO EVENTOS

  /* EVENTOS - ESTADÍSTICAS */
  {
    path: "/estadisticas",
    name: "Estadisticas",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Estadisticas.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },

  // LOGIN
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Usuario/Login.vue"),
    meta: { requiereAuth: false },
  },
  {
    path: "/login/:ruta/:idRuta",
    name: "LoginRoute",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Login.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  // PUENTE - SORTEO - G4S - 2024-01-27
  {
    path: "/login/:ruta",
    name: "LoginRedirect",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Login.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  {
    path: "/registrarse",
    name: "Registrarse",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Registrarse.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  // Registro que vuelve a una sección después de crear la cuenta (p. ej. /registrarse/resumen-en-vivo)
  {
    path: "/registrarse/:ruta",
    name: "RegistrarseRuta",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Registrarse.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  // PUENTE - SORTEO - G4S - 2024-01-27
  {
    path: "/registrarse/:ruta/:id",
    name: "RegistrarseEmpresaRutaVinculacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Registrarse.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  // SOPORTE TÉCNICO
  {
    path: "/soporte",
    name: "SoporteTecnico",
    component: () => import("../views/SoporteTecnico.vue"),
    meta: { requiereAuth: false },
  },
  // SESIÓN DE SOPORTE: soporte entra a la cuenta de un usuario desde el panel administrativo, sin
  // ver su contraseña (ver src/utils/sesionSoporte.js)
  {
    path: "/soporte/entrar",
    name: "SoporteEntrar",
    component: () => import("../views/Soporte/EntrarComoUsuario.vue"),
    meta: { requiereAuth: false },
  },
  // USUARIO
  {
    path: "/miperfil",
    name: "UsuarioMiPerfil",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Perfil/MiPerfil.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/miperfil/editar",
    name: "UsuarioMiPerfilEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Perfil/EditarPerfil.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // RESUMEN DEL EN VIVO (en el código, CapacitacionGratuita): video del en vivo y primeros pasos con
  // las pantallas en modo embebido. La dirección anterior redirige a la nueva.
  {
    path: "/capacitacion-gratuita",
    redirect: "/resumen-en-vivo",
  },
  {
    path: "/resumen-en-vivo",
    name: "CapacitacionGratuita",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/CapacitacionGratuita/CapacitacionGratuita.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CODIGO QR
  {
    path: "/codigoqr",
    name: "UsuarioCodigoQR",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/CodigoQR.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL
  {
    path: "/seguridadvial/inicio",
    name: "SeguridadVialInicio",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      const usuario = obtenerModoSinConexion ? await localforage.getItem(store.state.colecciones.localforage.usuarios.local) : auth.currentUser;
      if (usuario) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Inicio.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/Usuario/Login.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - SINCRONIZACIÓN - OFFLINE
  {
    path: "/seguridadvial/sincronizacion",
    name: "SeguridadVialSincronizacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Sincronizacion/Sincronizacion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - CONDUCTORES
  {
    path: "/seguridadvial/conductoresactivos",
    name: "SeguridadVialConductoresActivos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.conductores.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Conductores/Activos/Activos.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/conductoresinactivos",
    name: "SeguridadVialConductoresInactivos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.conductores.editar || obtenerVinculacion.privilegios.seguridadVial.conductores.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Conductores/Inactivos/Inactivos.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/conductoresactivos/editar/:id",
    name: "SeguridadVialConductoresEditarConductor",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      to.meta.consulta = null;
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          to.meta.component = await vistaHojaDeVidaConductor(to, obtenerUsuario, obtenerUsuario.preferencias.idEmpresaSeleccionada);
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
      // Lo pone vistaHojaDeVidaConductor cuando la hoja de vida es solo para consultar: { motivo, salida }
      consulta: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - VEHICULOS
  {
    path: "/seguridadvial/vehiculosactivos",
    name: "SeguridadVialVehiculosActivos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.vehiculos.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Vehiculos/Activos/Activos.vue")
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/vehiculosinactivos",
    name: "SeguridadVialVehiculosInactivos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.vehiculos.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Vehiculos/Inactivos/Inactivos.vue")
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/vehiculosactivos/editar/:id",
    name: "SeguridadVialVehiculosEditarVehiculo",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            to.meta.component = () => import("../views/SeguridadVial/Vehiculos/Vehiculo.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - FORMATOS
  {
    path: "/seguridadvial/formatos",
    name: "SeguridadVialFormatos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.formatos.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Formatos/Formatos.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/formatos/editar/:id",
    name: "SeguridadVialFormatosEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.formatos.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Formatos/Formato.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/formatos/consultar/:id",
    name: "SeguridadVialFormatosConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.formatos.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Formatos/Formato.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - CUESTIONARIOS
  {
    path: "/seguridadvial/cuestionarios",
    name: "SeguridadVialCuestionarios",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.formatos.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Cuestionarios/Cuestionarios.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/cuestionarios/editar/:id",
    name: "SeguridadVialCuestionariosEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.formatos.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Cuestionarios/Cuestionario.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/cuestionarios/consultar/:id",
    name: "SeguridadVialCuestionariosConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.formatos.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Cuestionarios/Cuestionario.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - INSPECCIONES - ONLINE
  {
    path: "/seguridadvial/inspecciones",
    name: "SeguridadVialInspecciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            // Quien no ve todas las inspecciones del espacio (sin el privilegio de eliminar) tiene las suyas en
            // Mis inspecciones; el aviso de llegada le cuenta dónde quedaron
            if (!obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
              return next({ name: "SeguridadVialMisInspecciones", query: { desde: "inspecciones" } });
            }
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspecciones.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - MIS INSPECCIONES
  {
    path: "/seguridadvial/misinspecciones",
    name: "SeguridadVialMisInspecciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      const obtenerUsuario = await store.state.obtenerUsuario(
        !obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "",
        obtenerModoSinConexion
      );
      if (obtenerUsuario && obtenerUsuario.preferencias && obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        const obtenerVinculacion = await store.state.obtenerVinculacion(
          obtenerUsuario.preferencias.idEmpresaSeleccionada,
          obtenerUsuario.id,
          obtenerModoSinConexion
        );
        if (obtenerVinculacion && obtenerVinculacion.estado === "Vinculado") {
          to.meta.component = () => import("../views/SeguridadVial/Inspecciones/MisInspecciones.vue");
        } else {
          to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
        }
      } else {
        to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
      }
      next();
    },
    meta: { requiereAuth: true, component: null },
  },
  // INSPECCIONES - FILTRO
  {
    path: "/seguridadvial/inspecciones/filtro",
    name: "SeguridadVialInspeccionesFiltro",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Filtro.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // INSPECCIONES - CONSOLIDADO
  {
    path: "/seguridadvial/inspecciones/consolidado",
    name: "SeguridadVialInspeccionesConsolidado",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Consolidado.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - INSPECCIONES - OFFLINE
  {
    path: "/seguridadvial/inspecciones/offline",
    name: "SeguridadVialInspeccionesOffline",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspecciones.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // NUEVA INSPECCION - INSPECCIONES - ONLINE
  {
    path: "/seguridadvial/inspecciones/nuevo",
    name: "SeguridadVialInspeccionesNuevo",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CARGAR INSPECCIÓN EN PAPEL - INSPECCIONES - ONLINE
  // Para quien administra las inspecciones del espacio (privilegio de eliminar): con la fecha y la hora del papel.
  // El auditor SOFU (Inspección de auditoría) entra sin vinculación: escoge el espacio en la vista
  {
    path: "/seguridadvial/inspecciones/cargar",
    name: "SeguridadVialInspeccionesCargarPapel",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else if (await esAuditorSofu(true)) {
        to.meta.component = () => import("../views/SeguridadVial/Inspecciones/CargarDesdePapel.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/CargarDesdePapel.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // EDITAR INSPECCIÓN CARGADA DESDE PAPEL - INSPECCIONES - ONLINE
  {
    path: "/seguridadvial/inspecciones/cargar/editar/:id",
    name: "SeguridadVialInspeccionesCargarPapelEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else if (await esAuditorSofu(true)) {
        to.meta.component = () => import("../views/SeguridadVial/Inspecciones/CargarDesdePapel.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/CargarDesdePapel.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // NUEVA INSPECCION - INSPECCIONES - OFFLINE
  {
    path: "/seguridadvial/inspecciones/offline/nuevo",
    name: "SeguridadVialInspeccionesOfflineNuevo",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // INSPECCION SIN RODAMIENTO (SIN RODAMIENTO Y SEMESTRAL) - ONLINE
  {
    path: "/seguridadvial/inspecciones/nuevo/conductor/:idConductor/vehiculo/:idVehiculo/rodamiento/:rodamiento/tipo/:tipo",
    name: "SeguridadVialInspeccionesNuevoSinRodamiento",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // INSPECCIÓN (PLAN DE DESPLAZAMIENTO TERRESTRE, CON RODAMIENTO Y PRE-OPERACIONAL) - ONLINE
  {
    path: "/seguridadvial/inspecciones/nuevo/plandesplazamiento/:idPlanDesplazamiento/conductor/:idConductor/vehiculo/:idVehiculo/supervisor/:idSupervisor/rodamiento/:rodamiento/tipo/:tipo",
    name: "SeguridadVialInspeccionesNuevoPlanDesplazamiento",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // INSPECCIÓN (PLAN DE DESPLAZAMIENTO TERRESTRE, CON RODAMIENTO Y PRE-OPERACIONAL) - OFFLINE
  {
    path: "/seguridadvial/inspecciones/offline/nuevo/plandesplazamiento/:idPlanDesplazamiento/conductor/:idConductor/vehiculo/:idVehiculo/supervisor/:idSupervisor/rodamiento/:rodamiento/tipo/:tipo",
    name: "SeguridadVialInspeccionesOfflineNuevoPlanDesplazamiento",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // EDITAR INSPECCION - INSPECCIONES - ONLINE
  {
    path: "/seguridadvial/inspecciones/editar/:id",
    name: "SeguridadVialInspeccionesEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // EDITAR INSPECCION - INSPECCIONES - OFFLINE
  {
    path: "/seguridadvial/inspecciones/offline/editar/:id",
    name: "SeguridadVialInspeccionesOfflineEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Inspeccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CONSULTAR INSPECCION - INSPECCIONES - ONLINE
  {
    path: "/seguridadvial/inspecciones/consultar/:id",
    name: "SeguridadVialInspeccionesConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else if (await esAuditorSofu()) {
        // El auditor SOFU consulta las que hace en espacios a los que no está vinculado
        to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Consultar.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Consultar.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CONSULTAR INSPECCION - INSPECCIONES - OFFLINE
  {
    path: "/seguridadvial/inspecciones/offline/consultar/:id",
    name: "SeguridadVialInspeccionesOfflineConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
          to.meta.component = () => import("../views/SeguridadVial/Inspecciones/Consultar.vue");
        } else {
          to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
        }
      } else {
        to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PLAN DE ACCIÓN - INSPECCIONES - ONLINE
  {
    path: "/seguridadvial/inspecciones/planaccion/:id",
    name: "SeguridadVialInspeccionesPlanAccion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Inspecciones/PlanAccion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - MANTENIMIENTOS
  {
    path: "/seguridadvial/mantenimientos",
    name: "SeguridadVialMantenimientos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.mantenimientos.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Mantenimientos/Mantenimientos.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/mantenimientos/nuevo",
    name: "SeguridadVialMantenimientosNuevo",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.mantenimientos.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Mantenimientos/Mantenimiento.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/mantenimientos/editar/:id",
    name: "SeguridadVialMantenimientosEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.mantenimientos.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Mantenimientos/Mantenimiento.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - SINIESTROS
  {
    path: "/seguridadvial/siniestros",
    name: "SeguridadVialSiniestros",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.siniestralidadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Siniestros/Siniestros.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // Informe Anual de Autogestión del PESV (issue #1187, RF-02). Requiere conexión.
    path: "/seguridadvial/informeautogestion",
    name: "SeguridadVialInformeAutogestion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else if (MODO_MUESTRA_INFORME_PESV) {
        // Solo desarrollo: espacio ficticio, sin espacio seleccionado ni privilegio (ver modoMuestra.js).
        to.meta.component = () => import("../views/SeguridadVial/InformeAutogestion/InformeAutogestion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          // Las vinculaciones creadas antes de este módulo no tienen el bloque informeAutogestion.
          const privilegio = obtenerVinculacion.privilegios.seguridadVial && obtenerVinculacion.privilegios.seguridadVial.informeAutogestion;
          if (obtenerVinculacion.estado === "Vinculado" && privilegio && privilegio.ver) {
            to.meta.component = () => import("../views/SeguridadVial/InformeAutogestion/InformeAutogestion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/siniestros/nuevo",
    name: "SeguridadVialSiniestrosNuevoSiniestro",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.siniestralidadVial.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Siniestros/Siniestro.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/siniestros/editar/:id",
    name: "SeguridadVialSiniestrosEditarSiniestro",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.siniestralidadVial.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Siniestros/Siniestro.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/siniestros/consultar/:id",
    name: "SeguridadVialSiniestrosConsultarSiniestro",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.siniestralidadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Siniestros/Siniestro.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - INFOGRAFIAS
  {
    path: "/seguridadvial/infografias",
    name: "SeguridadVialInfografias",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.infografias.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Infografias/Infografias.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/infografias/estadisticas",
    name: "SeguridadVialInfografiasEstadisticas",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.infografias.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Infografias/Estadisticas.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - NOVEDADES
  {
    path: "/seguridadvial/novedades",
    name: "SeguridadVialNovedades",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Novedades/Novedades.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/novedades/nuevo",
    name: "SeguridadVialNovedadesNuevo",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Novedades/Novedad.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/seguridadvial/novedades/consultar/:id",
    name: "SeguridadVialNovedadesConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Novedades/Consultar.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - PLANES DE DESPLAZAMIENTOS TERRESTRES - ONLINE
  {
    path: "/seguridadvial/planesdesplazamientos",
    name: "SeguridadVialPlanesDesplazamientos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/PlanesDesplazamientos.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - PLANES DE DESPLAZAMIENTOS TERRESTRES - FILTRO
  {
    path: "/seguridadvial/planesdesplazamientos/filtro",
    name: "SeguridadVialPlanesDesplazamientosFiltro",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/Filtro.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - PLANES DE DESPLAZAMIENTOS TERRESTRES - OFFLINE
  {
    path: "/seguridadvial/planesdesplazamientos/offline",
    name: "SeguridadVialPlanesDesplazamientosOffline",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/PlanesDesplazamientos.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PLANES DE DESPLAZAMIENTOS TERRESTRES - EDITAR PLAN - ONLINE
  {
    path: "/seguridadvial/planesdesplazamientos/editar/:id",
    name: "SeguridadVialPlanesDesplazamientosEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/PlanDesplazamiento.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PLANES DE DESPLAZAMIENTOS TERRESTRES - EDITAR PLAN - OFFLINE
  {
    path: "/seguridadvial/planesdesplazamientos/offline/editar/:id",
    name: "SeguridadVialPlanesDesplazamientosOfflineEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/PlanDesplazamiento.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PLANES DE DESPLAZAMIENTOS TERRESTRES - CONSULTAR PLAN - ONLINE
  {
    path: "/seguridadvial/planesdesplazamientos/consultar/:id",
    name: "SeguridadVialPlanesDesplazamientosConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/Consultar.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // PLANES DE DESPLAZAMIENTOS TERRESTRES - CONSULTAR PLAN - OFFLINE
  {
    path: "/seguridadvial/planesdesplazamientos/offline/consultar/:id",
    name: "SeguridadVialPlanesDesplazamientosOfflineConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/PlanesDesplazamientos/Consultar.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - APTITUDES FÍSICAS - ONLINE
  {
    path: "/seguridadvial/aptitudesfisicas",
    name: "SeguridadVialAptitudesFisicas",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/AptitudesFisicas.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE SEGURIDAD VIAL - APTITUDES FÍSICAS - OFFLINE
  {
    path: "/seguridadvial/aptitudesfisicas/offline",
    name: "SeguridadVialAptitudesFisicasOffline",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/AptitudesFisicas.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // APTITUDES FÍSICAS - FILTRO
  {
    path: "/seguridadvial/aptitudesfisicas/filtro",
    name: "SeguridadVialAptitudesFisicasFiltro",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/Filtro.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // APTITUDES FÍSICAS - CONSOLIDADO
  {
    path: "/seguridadvial/aptitudesfisicas/consolidado",
    name: "SeguridadVialAptitudesFisicasConsolidado",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.eliminar) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/Consolidado.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // NUEVA APTITUD FISICA - APTITUDES FISICAS - ONLINE
  {
    path: "/seguridadvial/aptitudesfisicas/nueva",
    name: "SeguridadVialAptitudesFisicasNueva",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/AptitudFisica.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // NUEVA APTITUD FISICA - APTITUDES FISICAS - OFFLINE
  {
    path: "/seguridadvial/aptitudesfisicas/offline/nueva",
    name: "SeguridadVialAptitudesFisicasOfflineNueva",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.crear) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/AptitudFisica.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // EDITAR APTITUD FISICA - APTITUDES FISICAS - ONLINE
  {
    path: "/seguridadvial/aptitudesfisicas/editar/:id",
    name: "SeguridadVialAptitudesFisicasEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/AptitudFisica.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // EDITAR APTITUD FISICA - APTITUDES FISICAS - OFFLINE
  {
    path: "/seguridadvial/aptitudesfisicas/offline/editar/:id",
    name: "SeguridadVialAptitudesFisicasOfflineEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.editar) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/AptitudFisica.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CONSULTAR APTITUD FISICA - APTITUDES FISICAS - ONLINE
  {
    path: "/seguridadvial/aptitudesfisicas/consultar/:id",
    name: "SeguridadVialAptitudesFisicasConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/Consultar.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CONSULTAR APTITUD FISICA - APTITUDES FISICAS - OFFLINE
  {
    path: "/seguridadvial/aptitudesfisicas/offline/consultar/:id",
    name: "SeguridadVialAptitudesFisicasOfflineConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        const obtenerUsuario = await store.state.obtenerUsuario(!obtenerModoSinConexion && auth.currentUser ? auth.currentUser.uid : "", obtenerModoSinConexion);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id, obtenerModoSinConexion);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.seguridadVial.inspecciones.ver) {
            to.meta.component = () => import("../views/SeguridadVial/Aptitudes/AptitudesFisicas/Consultar.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      } else {
        to.meta.component = () => import("../views/404.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE CAPACITACIONES - SUSCRIPCIONES
  /*{
    path: "/suscripcionCapacitaciones/:correo/:telefono",
    name: "SuscripcionCapacitaciones",
    component: () =>
      import("../views/landings/capacitaciones/SuscripcionCapacitaciones.vue"),
  },*/
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/:id/Creacionsecciones",
  //   name: "capacitacionesCursosSecciones",
  //   component: () => import("../views/Capacitaciones/Archivo/CreacionSecciones.vue"),
  //   meta: { requiereAuth: true },
  // },
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/categorias",
  //   name: "CapacitacionesCategorias",
  //   component: () => import("../views/Capacitaciones/Archivo/Categorias.vue"),
  //   meta: { requiereAuth: true },
  // },
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/:id/miscapacitaciones",
  //   name: "CapacitacionesMisCursos",
  //   component: () => import("../views/Capacitaciones/Archivo/MisCapacitaciones.vue"),
  //   meta: { requiereAuth: true },
  // },
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/:id/Creacionevaluacion",
  //   name: "CapacitacionesCursosEvaluacion",
  //   component: () => import("../views/Capacitaciones/Archivo/CreacionEvaluacion.vue"),
  //   meta: { requiereAuth: true },
  // },
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/:id/editar",
  //   name: "CapacitacionesCategoriasCursosEditar",
  //   component: () => import("../views/Capacitaciones/Archivo/EditarCurso.vue"),
  //   meta: { requiereAuth: true },
  // },
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/cursos/:id/editar/generales",
  //   name: "CapacitacionesCategoriasCursosEditarGenerales",
  //   component: () => import("../views/Capacitaciones/Archivo/EditarGenerales.vue"),
  //   meta: { requiereAuth: true },
  // },
  // ARCHIVADO
  // {
  //   path: "/capacitaciones/cursos/:id/editar/secciones",
  //   name: "CapacitacionesCategoriasCursosEditarSecciones",
  //   component: () => import("../views/Capacitaciones/Archivo/EditarSecciones.vue"),
  //   meta: { requiereAuth: true },
  // },
  // MODULO DE EMPRESAS, VINCULACIONES Y REGIONALES
  {
    path: "/empresas/inicio",
    name: "EmpresasInicio",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Empresa/Inicio.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/datosgenerales",
    name: "EmpresaDatosGenerales",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Empresa/DatosGenerales.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/misempresas",
    name: "EmpresasMisEmpresas",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Empresa/MisEmpresas/MisEmpresas.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/misempresas/crearempresa",
    name: "EmpresasMisEmpresasCrearEmpresa",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const datosCompletados = await store.state.validarDatosUsuario(auth.currentUser.uid);
        to.meta.component = () => datosCompletados ? import("../views/Empresa/MisEmpresas/CrearEmpresa.vue") : import("../components/Alertas/CompletarDatos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/misempresas/editarempresa/:id",
    name: "EmpresasMisEmpresasEditarEmpresa",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.editar) {
            to.meta.component = () => import("../views/Empresa/MisEmpresas/EditarEmpresa.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/misempresas/consultarempresa/:id",
    name: "EmpresasMisEmpresasConsultarEmpresa",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Empresa/MisEmpresas/ConsultarEmpresa.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /*{
    path: "/empresas/horarios",
    name: "EmpresasHorarios",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.horarios.ver) {
          return import("../views/Empresa/Horarios/Horarios.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/empresas/horarios/:id",
    name: "EmpresasHorario",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.horarios.editar) {
          return import("../views/Empresa/Horarios/Horario.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },*/
  {
    path: "/empresas/vinculaciones",
    name: "EmpresasVinculaciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.vinculaciones.ver) {
            to.meta.component = () => import("../views/Empresa/Vinculaciones/Vinculaciones.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/vinculaciones/notificaciones/:idVinculacion",
    name: "EmpresasVinculacionesNotificaciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.vinculaciones.desvincular) {
            to.meta.component = () => import("../views/Empresa/Vinculaciones/Notificaciones/Notificaciones.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/vinculaciones/nuevavinculacion",
    name: "EmpresasVinculacionesNuevaVinculacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.vinculaciones.crear) {
            to.meta.component = () => import("../views/Empresa/Vinculaciones/NuevaVinculacion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/vinculaciones/crearyvincular",
    name: "EmpresasVinculacionesCrearYVincular",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(
            obtenerUsuario.preferencias.idEmpresaSeleccionada,
            obtenerUsuario.id,
          );
          if (
            obtenerVinculacion.estado === "Vinculado" &&
            obtenerVinculacion.privilegios.empresa.vinculaciones.crear
          ) {
            to.meta.component = () =>
              import("../views/Empresa/Vinculaciones/CrearYVincularUsuario.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/gestiondocumental",
    name: "EmpresasGestionDocumental",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerUsuario.preferencias.idEmpresaSeleccionada);
          if (obtenerEmpresa && obtenerEmpresa.nit.consecutivo === 0) {
            const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
            if (obtenerVinculacion.estado === "Vinculado" && (obtenerVinculacion.privilegios.panelAdministrativo && obtenerVinculacion.privilegios.panelAdministrativo.ver)) {
              const existeRuta = await store.state.existeCarpetaStorage(`files/empresas/${obtenerUsuario.preferencias.idEmpresaSeleccionada}/gestion_documental`);
              if (existeRuta) {
                to.meta.component = () => import("../views/Empresa/GestionDocumental/GestionDocumental.vue");
              } else {
                to.meta.component = () => import("@/components/Alertas/SinGestionDocumental.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SeleccionarEmpresaPrincipal.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/solicitudesvinculacion',
    name: 'SolicitudesVinculacion',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const datosCompletados = await store.state.validarDatosUsuario(auth.currentUser.uid);
        to.meta.component = () => datosCompletados ? import("../views/Usuario/SolicitudesVinculacion/SolicitudesVinculacion.vue") : import("../components/Alertas/CompletarDatos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/solicitudesvinculacion/nuevasolicitud",
    name: "SolicitudesVinculacionNuevaSolicitud",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const datosCompletados = await store.state.validarDatosUsuario(auth.currentUser.uid);
        to.meta.component = () => datosCompletados ? import("../views/Usuario/SolicitudesVinculacion/NuevaSolicitud.vue") : import("../components/Alertas/CompletarDatos.vue");
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/invitacionesvinculacion",
    name: "EmpresasInvitacionesVinculacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.vinculaciones.ver) {
            to.meta.component = () => import("../views/Empresa/InvitacionesVinculacion/InvitacionesVinculacion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/invitacionesvinculacion/nuevainvitacion",
    name: "EmpresasInvitacionesVinculacionNuevaInvitacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.vinculaciones.crear) {
            to.meta.component = () => import("../views/Empresa/InvitacionesVinculacion/NuevaInvitacion.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/empresas/desvinculaciones",
    name: "EmpresasDesvinculaciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.vinculaciones.ver) {
            to.meta.component = () => import("../views/Empresa/Desvinculaciones/Desvinculaciones.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULOS PROFESIOGRAMAS
  {
    path: '/empresas/profesiogramas',
    name: 'EmpresasProfesiogramas',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.ver) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Profesiogramas.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/empresas/profesiogramas/consultar/:id',
    name: 'EmpresasProfesiogramasConsultarProfesiograma',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.ver) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Consultar.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /*{
    path: '/empresas/profesiogramas/editar/:id',
    name: 'EmpresasProfesiogramasEditarProfesiograma',
    component: () => import("../views/Empresa/Profesiogramas/Profesiograma.vue"),
    meta: { requiereAuth: true }
  },*/
  {
    path: '/empresas/profesiogramas/cargos',
    name: 'EmpresasProfesiogramasCargos',
    redirect: { name: "EmpresasProfesiogramas" },
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.ver) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Categorias/Cargos/Cargos.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/empresas/profesiogramas/cargos/nuevo',
    name: 'EmpresasProfesiogramasCargosNuevo',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.crear) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Categorias/Cargos/NuevoCargo.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/empresas/profesiogramas/cargos/editar/:id',
    name: 'EmpresasProfesiogramasCargosEditar',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.editar) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Categorias/Cargos/EditarCargo.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/empresas/profesiogramas/examenes',
    name: 'EmpresasProfesiogramasExamenes',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.ver) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Examenes/Examenes.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/empresas/profesiogramas/categorias/cargos',
    name: 'EmpresasProfesiogramasCategoriasCargos',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado") {
            const obtenerEmpresa = await store.state.obtenerEmpresa(obtenerVinculacion.idEmpresa);
            if (obtenerEmpresa) {
              const nit = obtenerEmpresa.nit.numero;
              const obtenerEmpresaPrincipal = await store.state.obtenerEmpresaPrincipal(nit);
              if (obtenerEmpresaPrincipal) {
                const obtenerVinculacionPrincipal = await store.state.obtenerVinculacion(obtenerEmpresaPrincipal.id, obtenerUsuario.id);
                if (obtenerVinculacionPrincipal.estado === "Vinculado" && obtenerVinculacionPrincipal.privilegios.empresa.profesiogramas.ver) {
                  to.meta.component = () => import("../views/Empresa/Profesiogramas/Categorias/Categorias.vue");
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
            }
          } else {
            to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
          }
        } else {
          to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE EMPRESA-REGIONALES
  /*{
    path: "/empresas/misregionales",
    name: "EmpresasMisRegionales",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.regionales.ver) {
          return import("../views/Empresa/MisRegionales/MisRegionales.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/empresas/misregionales/crearregional",
    name: "EmpresasMisRegionalesCrearRegional",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.regionales.crear) {
          return import("../views/Empresa/MisRegionales/CrearRegional.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/empresas/misregionales/editarregional/:id",
    name: "EmpresasMisRegionalesEditarRegional",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.empresa.regionales.editar) {
          return import("../views/Empresa/MisRegionales/EditarRegional.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: {
      requiereAuth: true,
    },
  },
  {
    path: "/empresas/misregionales/misinvitaciones",
    name: "EmpresasMisRegionalesMisInvitaciones",
    component: () => import("../views/Empresa/MisRegionales/Invitaciones.vue"),
    meta: { requiereAuth: true },
  },
  {
    path: "/empresas/misregionales/misinvitaciones/:id/:estado",
    name: "EmpresasMisRegionalesEstado",
    component: () => import("../views/Empresa/MisRegionales/Estado.vue"),
    meta: { requiereAuth: true },
  },*/
  // PLANES DE ACCIÓN
  {
    path: '/planesaccion',
    name: 'PlanesAccion',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const usuario = auth.currentUser;
        if (usuario) {
          const obtenerVinculaciones = await store.state.obtenerVinculacionesPlanesAccion(auth.currentUser.uid);
          if (obtenerVinculaciones.length > 1) {
            to.meta.component = () => import("../views/PlanesAccion/PlanesAccion.vue");
          } else {
            const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
            if (obtenerUsuario && obtenerUsuario.preferencias.idEmpresaSeleccionada) {
              const obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
              if (obtenerVinculacion.estado === "Vinculado" && (obtenerVinculacion.privilegios.panelAdministrativo && obtenerVinculacion.privilegios.panelAdministrativo.ver)) {
                const obtenerUltimoPlanAccion = await store.state.planAccion.registro.funciones.obtenerUltimo(obtenerUsuario.preferencias.idEmpresaSeleccionada);
                if (obtenerUltimoPlanAccion) {
                  next({ name: "PlanesAccionEditar", params: { id: obtenerUltimoPlanAccion.id } });
                } else {
                  to.meta.component = () => import("@/components/Alertas/SinPlanAccion.vue");
                }
              } else {
                to.meta.component = () => import("@/components/Alertas/SinPrivilegios.vue");
              }
            } else {
              to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
            }
          }
        } else {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/misactividades',
    name: 'PlanesAccionActividades',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const usuario = auth.currentUser;
        if (usuario) {
          const obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
          if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
            to.meta.component = () => import("../views/PlanesAccion/Actividades/Actividades.vue");
          } else {
            to.meta.component = () => import("@/components/Alertas/SinEmpresa.vue");
          }
        } else {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // EDITAR PLAN DE ACCIÓN - PLANES DE ACCIÓN
  {
    path: '/planesaccion/editar/:id',
    name: 'PlanesAccionEditar',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const usuario = auth.currentUser;
        if (usuario) {
          to.meta.component = () => import("../views/PlanesAccion/PlanAccion.vue");
        } else {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CONSULTAR PLAN DE ACCIÓN - PLANES DE ACCIÓN
  {
    path: '/planesaccion/consultar/:id',
    name: 'PlanesAccionConsultar',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        const usuario = auth.currentUser;
        if (usuario) {
          to.meta.component = () => import("../views/PlanesAccion/PlanAccion.vue");
        } else {
          to.meta.component = () => import("../views/Usuario/Login.vue");
        }
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE CAPACITACIONES
  {
    path: '/capacitaciones/inicio',
    name: 'CapacitacionesInicio',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Inicio.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // MODULO DE CAPACITACIONES
  {
    path: '/capacitaciones/validador/certificado',
    name: 'CapacitacionesValidadorCertificado',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/ValidadorCertificado/Validador.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // SOLID + HTML5 SEMANTIC + REFACTOR
    path: "/capacitaciones",
    name: "Capacitaciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Capacitaciones.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // PUENTE - SORTEO - G4S - 2024-01-27
    path: "/capacitaciones/eventos/:id",
    name: "CapacitacionesEventos",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Capacitaciones.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // SOLID + HTML5 SEMANTIC + REFACTOR
    path: "/capacitaciones/:id",
    name: "Capacitacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (!obtenerModoSinConexion && to.params.id) {
        const estado = await store.state.obtenerValidacionCapacitacion(to.params.id);
        if (estado !== true) return next({ name: "Capacitaciones" });
        to.meta.component = () => import("../views/Capacitaciones/Capacitacion.vue");
      } else if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        return next({ name: "Capacitaciones" });
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // SOLID + HTML5 SEMANTIC + REFACTOR
    path: "/capacitaciones/capacitacion/nueva",
    name: "CapacitacionesNuevo",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Editar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // SOLID + HTML5 SEMANTIC + REFACTOR
    path: "/capacitaciones/editar/:id",
    name: "CapacitacionesEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Editar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // SOLID + HTML5 SEMANTIC + REFACTOR
    path: "/capacitaciones/evaluar/:id",
    name: "CapacitacionesEvaluar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      if (!obtenerModoSinConexion && to.params.id) {
        const estado = await store.state.obtenerValidacionCapacitacion(to.params.id);
        if (estado !== true) return next({ name: "Capacitaciones" });
        to.meta.component = () => import("../views/Capacitaciones/Capacitacion.vue");
      } else if (obtenerModoSinConexion) {
        to.meta.component = () => import("../components/Alertas/SinConexion.vue");
      } else {
        return next({ name: "Capacitaciones" });
      }
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    // PENDIENTE - SOLID + HTML5 SEMANTIC + REFACTOR
    path: "/capacitaciones/:id/evaluacion",
    name: "CapacitacionesEvaluacion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Evaluaciones/Evaluacion.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: '/capacitaciones/:id/visualizador/:id1',
    name: 'CapacitacionesVisualizador',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Visualizador.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/capacitaciones/estadisticas",
    name: "CapacitacionesEstadisticas",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Evaluaciones/Evaluaciones.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/capacitaciones/:id/estadisticas",
    name: "CapacitacionesEstadisticasCurso",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Capacitaciones/Estadisticas/TrabajadoresEvaluados.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /* 404 - NOT FOUND */
  {
    path: "*",
    name: "NotFound",
    component: () => import("../views/404.vue"),
    meta: { requiereAuth: false },
  },
  /* CONTROL DE PERSONAL */
  /*{
    path: "/controlpersonal/inicio",
    name: "ControlPersonalInicio",
    component: async () => {
      if (auth.currentUser) {
        let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
        if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
          let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
          if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.controlPersonal.ver) {
            return import("../views/ControlPersonal/Inicio.vue")
          } else {
            return import("@/components/Alertas/SinPrivilegios.vue")
          }
        } else {
          return import("@/components/Alertas/SinEmpresa.vue")
        }
      } else {
        return import("../views/Usuario/Login.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/controlpersonal/controldiario",
    name: "ControlDiario",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.controlPersonal.controlDiario) {
          return import("../views/ControlPersonal/ControlDiario.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/controlpersonal/registromanual",
    name: "RegistroManual",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.controlPersonal.registroManual) {
          return import("../views/ControlPersonal/RegistroManual.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/controlpersonal/registroqr",
    name: "RegistroQR",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.controlPersonal.registrarQR) {
          return import("../views/ControlPersonal/RegistroQR.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/controlpersonal/estadisticas",
    name: "Estadisticas",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.controlPersonal.estadisticas) {
          return import("../views/ControlPersonal/Estadisticas.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/controlpersonal/ausentismolaboral",
    name: "AusentismoLaboral",
    component: async () => {
      let obtenerUsuario = await store.state.obtenerUsuario(auth.currentUser.uid);
      if (obtenerUsuario.preferencias.idEmpresaSeleccionada) {
        let obtenerVinculacion = await store.state.obtenerVinculacion(obtenerUsuario.preferencias.idEmpresaSeleccionada, obtenerUsuario.id);
        if (obtenerVinculacion.estado === "Vinculado" && obtenerVinculacion.privilegios.controlPersonal.ausentismo) {
          return import("../views/ControlPersonal/AusentismoLaboral.vue")
        } else {
          return import("@/components/Alertas/SinPrivilegios.vue")
        }
      } else {
        return import("@/components/Alertas/SinEmpresa.vue")
      }
    },
    meta: { requiereAuth: true },
  },
  {
    path: "/controlpersonal/registroausentismo",
    name: "RegistroAusentismo",
    component: () => import("../views/ControlPersonal/RegistroAusentismo.vue"),
    meta: { requiereAuth: true },
  },*/
  // CENTRO DE AYUDA - PREGUNTAS FRECUENTES
  {
    path: "/ayuda",
    name: "Ayuda",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Ayuda.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },
  /*{
    path: "/desarrollo/iper",
    name: "IPER",
    component: () => import("../views/Desarrollo/IPER/IPER.vue"),
    meta: {
      requiereAuth: true,
    },
  },*/
  /*{
    path: "/desarrollo/mapageneral",
    name: "MapaGeneralDesarrollo",
    component: () => import("../views/Desarrollo/MapaGeneral.vue"),
    meta: {
      requiereAuth: true,
    },
  },*/
  // INSPECCIONES OFFLINE
  /*{
    path: "/desarrollo/offline/inspecciones",
    name: "DesarrolloOfflineInspecciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Offline/Inspecciones.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/desarrollo/offline/inspecciones/nuevo",
    name: "DesarrolloOfflineNuevaInspeccion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Offline/Inspecciones.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/desarrollo/offline/inspecciones/editar/:id",
    name: "DesarrolloOfflineEditarInspeccion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Offline/Inspeccion.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/desarrollo/offline/inspecciones/consultar/:id",
    name: "DesarrolloOfflineConsultarInspeccion",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Offline/Consultar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/
  // PLANES
  {
    path: "/planes",
    name: "Planes",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Planes/Planes.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /* NOTIFICACIONES */
  {
    path: "/notificaciones",
    name: "Notificaciones",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Notificaciones/Notificaciones.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  {
    path: "/notificaciones/notificacion/:id",
    name: "NotificacionesVer",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Usuario/Notificaciones/Notificacion.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // DESARROLLO
  {
    path: '/certificado',
    name: 'Certificado',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Certificado.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // DASHBOARD - V1 - PEDRO
  {
    path: '/dev/dashboard',
    name: 'DevDashboard',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Dashboard.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // DASHBOARD - V1 - ARIEL
  {
    path: '/dev/paneldashboard',
    name: 'DevPanelDashboard',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/PanelDashboard.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // CONSOLIDADO - PEDRO
  {
    path: '/consolidado',
    name: 'Consolidado',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Consolidado.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // DASHBOARD - V2 - ARIEL
  {
    path: '/paneladministrativo/dashboard',
    name: 'PanelAdministrativoDashboardV2',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/PanelAdministrativo/DashboardEspaciosTrabajo/Dashboard.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },

  // DASHBOARD - ADMIN
  {
    path: '/admin/migracion',
    name: 'AdminMigracion',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Admin/Inicio.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /* FIN - PANEL ADMIN */

  // DASHBOARD - V3
  {
    path: '/administracion',
    name: 'Administracion',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Administracion/Inicio.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  /* FIN - PANEL ADMIN */

  // CONSOLIDADO VEHÍCULOS
  {
    path: '/consolidadovehiculos',
    name: 'ConsolidadoVehiculos',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/ConsolidadoVehiculos.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },
  // REPORTES
  {
    path: '/reportes',
    name: 'Reportes',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Desarrollo/Reportes.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },

  // PWA
  /* {
    path: '/instalar',
    name: 'InstalarPWA',
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/InstalarPWA.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  }, */
  {
    path: "/instalar",
    name: "InstalarPWA",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      to.meta.component = () => import("../views/InstalarPWA.vue");
      next();
    },
    meta: {
      requiereAuth: false,
      component: null,
    },
  },

  /* ---------------------- RUTOGRAMAS --------------------------- */

  /* ---------------------- OFFLINE --------------------------- */

  /* RUTOGRAMAS - OFFLINE */
  /*{
    path: "/rutogramas/offline",
    name: "RutogramasOffline",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      to.meta.component = () => import("../views/Rutogramas/Rutogramas.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/
  /* RUTOGRAMAS - EDITAR REGISTRO - (OFFLINE) */
  /*{
    path: "/rutogramas/offline/editar/:id",
    name: "RutogramasOfflineEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      to.meta.component = () => import("../views/Rutogramas/Editar.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/
  /* RUTOGRAMAS - CONSULTAR REGISTRO - (OFFLINE) */
  /*{
    path: "/rutogramas/offline/consultar/:id",
    name: "RutogramasOfflineConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      to.meta.component = () => import("../views/Rutogramas/Consultar.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/

  /* ---------------------- ONLINE --------------------------- */

  /* RUTOGRAMAS - ONLINE */
  /*{
    path: "/rutogramas/online",
    name: "RutogramasOnline",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Rutogramas/Rutogramas.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/
  /* RUTOGRAMAS - EDITAR REGISTRO - (OFFLINE) */
  /*{
    path: "/rutogramas/online/editar/:id",
    name: "RutogramasOnlineEditar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Rutogramas/Editar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/
  /* RUTOGRAMAS - CONSULTAR REGISTRO - (OFFLINE) */
  /*{
    path: "/rutogramas/online/consultar/:id",
    name: "RutogramasOnlineConsultar",
    component: DynamicWrapper,
    props: (route) => ({ component: route.meta.component }),
    beforeEnter: async (to, from, next) => {
      const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
      to.meta.component = () => !obtenerModoSinConexion ? import("../views/Rutogramas/Consultar.vue") : import("../components/Alertas/SinConexion.vue");
      next();
    },
    meta: {
      requiereAuth: true,
      component: null,
    },
  },*/

  /*{
    path: '/funciones',
    name: 'Funciones',
    component: () => import("../views/Desarrollo/Funciones.vue"),
    meta: { requiereAuth: true }
  },*/
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },
});

function restaurarScrollYLimpiarBloqueos() {
  // 1. Cerrar modales Bootstrap activos ordinarios (excluyendo términos obligatorios)
  try {
    /* eslint-env jquery */
    if (typeof window !== "undefined" && window.$ && typeof window.$.fn?.modal === "function") {
      $(".modal:not(#ModalAceptarTerminosObligatorios)").modal("hide");
    }
  } catch (e) {
    // Silenciar si jQuery no está inicializado
  }

  // 2. Limpiar elementos y overlays de Shepherd (Guías interactivas)
  const overlays = document.querySelectorAll(".shepherd-modal-overlay-container");
  for (var i = 0; i < overlays.length; i++) {
    overlays[i].remove();
  }
  const steps = document.querySelectorAll(".shepherd-element");
  for (var j = 0; j < steps.length; j++) {
    steps[j].remove();
  }

  // 3. Verificar si el modal de términos obligatorios está abierto
  const modalTerminosAbierto = typeof document !== "undefined" && document.querySelector("#ModalAceptarTerminosObligatorios.show");

  // 4. Remover backdrops huérfanos de Bootstrap si no hay modal obligatorio activo
  if (!modalTerminosAbierto) {
    const backdrops = document.querySelectorAll(".modal-backdrop");
    for (var k = 0; k < backdrops.length; k++) {
      backdrops[k].remove();
    }
  }

  // 5. Limpieza incondicional de propiedades de overflow en body y html (solo si no hay modal obligatorio activo)
  if (typeof document !== "undefined" && !modalTerminosAbierto) {
    if (document.body) {
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("overflow-y");
      document.body.style.removeProperty("padding-right");
      document.body.classList.remove("modal-open", "swal2-shown", "swal2-height-auto");
    }
    if (document.documentElement) {
      document.documentElement.style.removeProperty("overflow");
      document.documentElement.style.removeProperty("overflow-y");
      document.documentElement.classList.remove("swal2-shown", "swal2-height-auto");
    }

    // 6. Restaurar interactividad en enlaces y tarjetas
    document.querySelectorAll(".card.pointer-none, a.pointer-none").forEach((item) => {
      item.classList.remove("pointer-none");
    });
  }
}

let timerCarga = null;

router.beforeEach(async (to, from, next) => {
  const obtenerModoSinConexion = await store.state.obtenerModoSinConexion();
  const usuario = obtenerModoSinConexion ? await localforage.getItem(store.state.colecciones.localforage.usuarios.local) : auth.currentUser;
  const requiereAuth = to.matched.some((record) => record.meta.requiereAuth);
  
  restaurarScrollYLimpiarBloqueos();

  // Dentro de la Capacitación gratuita (modo embebido) la guía ya la revisó la página principal:
  // revisarla también en el iframe podía crear dos documentos de guía a un usuario nuevo.
  if (!obtenerModoSinConexion && !esEmbebido()) {
    if (usuario) {
      const obtenerGuia = await store.state.obtenerGuia(usuario.uid);
      if (!obtenerGuia) {
        const guia = new store.state.guia();
        guia.idUsuario = usuario.uid;
        await store.state.crearGuia(guia);
      }
    }
  }
  if (!Swal.isVisible()) {
    router.app.$isLoading(true);
  }
  // Condicionales de acceso / redireccionamiento
  // Si requiere autentificacion y tiene usuario
  if (requiereAuth && usuario) {
    next();
  }
  // Si requiere autentificacion y no tiene usuario
  else if (requiereAuth && !usuario) {
    // PUENTE - SORTEO - G4S - 2024-01-27
    if (
      to.name === "Capacitacion" ||
      to.name === "Capacitaciones" ||
      to.name === "CapacitacionesEventos"
    ) {
      next({ name: "LoginRedirect", params: { ruta: "g4s" } });
    } else if (to.name === "CapacitacionGratuita") {
      // El enlace del correo de la capacitación: después de entrar o registrarse vuelve aquí
      next({ name: "LoginRedirect", params: { ruta: "resumen-en-vivo" } });
    } else {
      next({ name: "Login" });
    }
  }
  // No requiere autentificacion y tiene usuario
  else if (!requiereAuth && usuario) {
    if (to.name === "Login" || to.name === "Registrarse") {
      next({ name: "Inicio" });
    } else if ((to.name === "LoginRedirect" || to.name === "RegistrarseRuta") && to.params.ruta === "resumen-en-vivo") {
      next({ name: "CapacitacionGratuita" });
    } else {
      next();
    }
  }
  // No requiere autentificacion y no tiene usuario
  else if (!requiereAuth && !usuario) {
    next();
  }
});

router.afterEach(() => {
  restaurarScrollYLimpiarBloqueos();
  const contenedor = document.getElementById("contenedor");

  if (timerCarga) {
    clearTimeout(timerCarga);
  }
  timerCarga = setTimeout(() => {
    if (router.app && router.app.$isLoading) {
      router.app.$isLoading(false);
    }
    restaurarScrollYLimpiarBloqueos();
  }, 400);

  setTimeout(() => {
    if (contenedor && contenedor.querySelector("div")) {
      const primerHijo = contenedor.querySelector("div");
      if (primerHijo && primerHijo.className) {
        const elemento = document.querySelector(`.${primerHijo.className}`);
        if (elemento) {
          elemento.removeAttribute("component");
        }
      }
    }
  }, 100);
});

router.onError((error) => {
  const isChunkLoadFailed =
    /Loading chunk .* failed/i.test(error.message) ||
    /Loading CSS chunk .* failed/i.test(error.message) ||
    error.name === "ChunkLoadError";
  if (isChunkLoadFailed) {
    console.warn("Se detectó una nueva versión de la aplicación desplegada. Recargando automáticamente...");
    window.location.reload();
  }
});

export default router;